import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/3a3e6aa4-58a0-4ab4-a6d7-f8a1be89a8f3.png" 
              alt="PELEGOR Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className={`text-2xl font-bold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              PELEGOR, d.o.o.
            </span>
          </div>
          <div className="flex gap-12">
            <a href="#home" className={`text-base font-medium transition-colors hover:text-[#FF6B6B] ${scrolled ? 'text-gray-700' : 'text-white'}`}>
              Home
            </a>
            <a href="#about" className={`text-base font-medium transition-colors hover:text-[#FF6B6B] ${scrolled ? 'text-gray-700' : 'text-white'}`}>
              About
            </a>
            <a href="#products" className={`text-base font-medium transition-colors hover:text-[#FF6B6B] ${scrolled ? 'text-gray-700' : 'text-white'}`}>
              Products
            </a>
            <a href="#features" className={`text-base font-medium transition-colors hover:text-[#FF6B6B] ${scrolled ? 'text-gray-700' : 'text-white'}`}>
              Features
            </a>
            <a href="#contact" className={`text-base font-medium transition-colors hover:text-[#FF6B6B] ${scrolled ? 'text-gray-700' : 'text-white'}`}>
              Contact
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f3a] via-[#2d1b4e] to-[#4a1d5f]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        
        {/* Abstract geometric shapes */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 w-full px-20 py-32 flex items-center justify-between">
          <div className="w-[45%]">
            <h1 className="text-7xl font-extrabold text-white leading-tight mb-6">
              Modern iOS Applications for Enhanced Security
            </h1>
            <p className="text-2xl text-gray-300 leading-relaxed">
              Expert team developing VPN and Cleaner solutions for iOS platform
            </p>
          </div>
          
          <div className="w-[55%] flex justify-center items-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B6B]/20 to-[#4ECDC4]/20 rounded-3xl blur-3xl"></div>
              <img 
                src="https://public.readdy.ai/ai/img_res/3fa80485daa7cffb41210140f5a7c503.jpg" 
                alt="iPhone VPN Interface"
                className="relative w-full h-auto object-contain transform rotate-6 hover:rotate-3 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-end mb-20">
            <div className="w-[65%]">
              <span className="text-xs font-bold text-[#FF6B6B] tracking-[2px] uppercase mb-4 block">
                WHO WE ARE
              </span>
              <h2 className="text-6xl font-bold text-gray-900 leading-tight mb-6">
                Building Tomorrow's Mobile Experience
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-[480px]">
                PELEGOR, d.o.o. is a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine cutting-edge technology with intuitive design to deliver solutions that protect and optimize your digital life.
              </p>
            </div>
          </div>
          
          <div className="flex gap-8">
            <div className="w-[38%]">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20software%20development%20team%20collaborating%20on%20iOS%20mobile%20applications%20in%20modern%20bright%20office%20space%20with%20warm%20natural%20lighting%20developers%20working%20together%20on%20MacBooks%20and%20iPhones%20contemporary%20workspace%20atmosphere%20clean%20minimalist%20background&width=450&height=550&seq=about-team-001&orientation=portrait" 
                alt="Team Collaboration"
                className="w-full h-[550px] object-cover rounded-3xl"
              />
            </div>
            <div className="w-[62%]">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20technology%20office%20workspace%20with%20multiple%20iOS%20devices%20iPhones%20and%20iPads%20on%20clean%20white%20desk%20contemporary%20minimalist%20setup%20with%20soft%20natural%20lighting%20professional%20development%20environment%20sleek%20design%20aesthetic%20bright%20airy%20atmosphere&width=750&height=550&seq=about-workspace-001&orientation=landscape" 
                alt="Modern Office Workspace"
                className="w-full h-[550px] object-cover rounded-3xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-20 bg-gradient-to-b from-white to-[#F8FAFF]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <span className="text-xs font-bold text-[#FF6B6B] tracking-[2px] uppercase mb-4 block">
              OUR PRODUCTS
            </span>
            <h2 className="text-7xl font-bold text-[#1a1f3a]">
              VPN &amp; Cleaner Solutions
            </h2>
          </div>
          
          <div className="flex gap-6">
            <div className="w-[35%] bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:scale-105">
              <div className="relative h-[400px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=premium%20VPN%20mobile%20application%20interface%20on%20iPhone%20screen%20showing%20secure%20connection%20with%20glowing%20neon%20green%20shield%20icon%20and%20lock%20symbol%20dark%20elegant%20UI%20design%20with%20connection%20status%20modern%20security%20app%20aesthetic%20clean%20simple%20background&width=420&height=400&seq=product-vpn-001&orientation=squarish" 
                  alt="VPN Product"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00FF88]/20 rounded-xl mb-4">
                    <i className="ri-shield-check-line text-3xl text-[#00FF88]"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-3">VPN Security</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    Protect your online privacy with military-grade encryption. Browse securely and anonymously on any network.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="w-[30%] bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:scale-105">
              <div className="relative h-[400px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20application%20interface%20showing%20storage%20optimization%20with%20bright%20cyan%20broom%20icon%20and%20sparkle%20cleaning%20effects%20modern%20mobile%20app%20design%20with%20progress%20indicators%20clean%20minimalist%20UI%20simple%20neutral%20background&width=360&height=400&seq=product-cleaner-001&orientation=squarish" 
                  alt="Cleaner Product"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#00D9FF]/20 rounded-xl mb-4">
                    <i className="ri-brush-line text-3xl text-[#00D9FF]"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-3">Smart Cleaner</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    Optimize device performance by removing junk files and freeing up valuable storage space.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="w-[35%] bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:scale-105">
              <div className="relative h-[400px]">
                <img 
                  src="https://readdy.ai/api/search-image?query=mobile%20security%20suite%20application%20interface%20with%20vibrant%20purple%20security%20badge%20icon%20and%20protection%20status%20dashboard%20on%20iPhone%20modern%20iOS%20app%20design%20showing%20comprehensive%20device%20protection%20features%20clean%20simple%20background&width=420&height=400&seq=product-security-001&orientation=squarish" 
                  alt="Security Suite"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#A855F7]/20 rounded-xl mb-4">
                    <i className="ri-shield-star-line text-3xl text-[#A855F7]"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-3">Security Suite</h3>
                  <p className="text-base text-white/90 leading-relaxed">
                    Complete protection suite combining VPN security with device optimization for ultimate peace of mind.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-40 px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-7xl font-bold text-gray-900 mb-20 ml-20">
            Why Choose Our Solutions 🚀
          </h2>
          
          <div className="flex gap-6">
            <div className="w-[32%] rounded-2xl overflow-hidden group cursor-pointer hover:scale-105 transition-transform duration-300">
              <div className="relative aspect-[3/4]">
                <img 
                  src="https://readdy.ai/api/search-image?query=iPhone%20screen%20displaying%20active%20VPN%20connection%20interface%20with%20secure%20tunnel%20visualization%20and%20connection%20status%20modern%20iOS%20app%20design%20showing%20encrypted%20network%20protection%20dark%20elegant%20UI%20clean%20simple%20background&width=380&height=500&seq=feature-vpn-001&orientation=portrait" 
                  alt="VPN Connection Feature"
                  className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/90 via-[#0f172a]/50 to-[#0f172a]/20"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-4">Instant Protection</h3>
                  <p className="text-base text-white/85 leading-relaxed">
                    Connect to secure servers worldwide with a single tap. Experience lightning-fast speeds without compromising security.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="w-[32%] rounded-2xl overflow-hidden group cursor-pointer hover:scale-105 transition-transform duration-300">
              <div className="relative aspect-[3/4]">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20device%20cleaning%20process%20interface%20showing%20storage%20analysis%20and%20junk%20file%20removal%20progress%20with%20animated%20indicators%20modern%20mobile%20app%20design%20optimization%20dashboard%20clean%20simple%20background&width=380&height=500&seq=feature-clean-001&orientation=portrait" 
                  alt="Cleaning Process Feature"
                  className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/90 via-[#0f172a]/50 to-[#0f172a]/20"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-4">Smart Optimization</h3>
                  <p className="text-base text-white/85 leading-relaxed">
                    Automatically identify and remove unnecessary files. Reclaim gigabytes of storage with intelligent cleaning algorithms.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="w-[32%] rounded-2xl overflow-hidden group cursor-pointer hover:scale-105 transition-transform duration-300">
              <div className="relative aspect-[3/4]">
                <img 
                  src="https://readdy.ai/api/search-image?query=comprehensive%20security%20dashboard%20on%20iPhone%20showing%20device%20protection%20status%20threat%20monitoring%20and%20privacy%20controls%20modern%20iOS%20security%20app%20interface%20with%20status%20indicators%20clean%20simple%20background&width=380&height=500&seq=feature-security-001&orientation=portrait" 
                  alt="Security Dashboard Feature"
                  className="w-full h-full object-cover group-hover:brightness-110 transition-all duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/90 via-[#0f172a]/50 to-[#0f172a]/20"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-3xl font-bold text-white mb-4">Real-time Monitoring</h3>
                  <p className="text-base text-white/85 leading-relaxed">
                    Stay informed with comprehensive security reports. Monitor your device health and privacy status in real-time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-20 bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <span className="text-xs font-bold text-[#FF6B6B] tracking-[2px] uppercase mb-4 block">
              GET IN TOUCH
            </span>
            <h2 className="text-7xl font-bold text-gray-900">
              Contact Us
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl shadow-xl p-16">
            <div className="grid grid-cols-2 gap-16">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-8">Our Location</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B6B]/10 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-[#FF6B6B]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Address</p>
                      <p className="text-base text-gray-900">Regenova ulica 1</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B6B]/10 rounded-xl flex-shrink-0">
                      <i className="ri-building-line text-2xl text-[#FF6B6B]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">City</p>
                      <p className="text-base text-gray-900">Maribor</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B6B]/10 rounded-xl flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-[#FF6B6B]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Zip</p>
                      <p className="text-base text-gray-900">2000</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B6B]/10 rounded-xl flex-shrink-0">
                      <i className="ri-global-line text-2xl text-[#FF6B6B]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Country</p>
                      <p className="text-base text-gray-900">Slovenia</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#4ECDC4]/10 rounded-xl flex-shrink-0">
                      <i className="ri-mail-send-line text-2xl text-[#4ECDC4]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Email</p>
                      <a href="mailto:contact@pelegordoo.com" className="text-base text-[#4ECDC4] hover:underline">
                        contact@pelegordoo.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#4ECDC4]/10 rounded-xl flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-[#4ECDC4]"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">Number</p>
                      <a href="tel:+18207320637" className="text-base text-gray-900 hover:text-[#4ECDC4]">
                        +1 8207320637
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-16 px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/3a3e6aa4-58a0-4ab4-a6d7-f8a1be89a8f3.png" 
                  alt="PELEGOR Logo" 
                  className="h-16 w-16 object-contain"
                />
                <div>
                  <h3 className="text-5xl font-extrabold">PELEGOR</h3>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="w-20 h-0.5 bg-gray-600"></div>
                    <span className="text-base text-gray-400">d.o.o.</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-6">
                © 2025 PELEGOR, d.o.o. All rights reserved.
              </p>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-gray-400 mb-2">Expert iOS Development Team</p>
              <p className="text-base text-gray-300">Building Secure Mobile Solutions</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}